/**
 * PERSON KANBAN CARD — adattatore entity-specific della card contatto.
 *
 * Non ridisegna lo scheletro (root/top/titolo/meta/foot): quello è di {@link KbCard}, condiviso con
 * preventivi e task. Qui si passano solo i dati del contatto: avatar con l'iniziale, nome e cognome
 * (link al dettaglio), località e stato lead nel `meta`, i recapiti principali nel `foot`.
 *
 * <b>I recapiti non costano una chiamata.</b> `emailAddresses` e `phones` arrivano già dentro il
 * DTO del contatto — il backend li risolve in `toPerson` — quindi la card legge quello che ha già
 * in mano e non va a cercare nulla.
 *
 * Lo stato lead usa la pill neutra `kb-card__tag` e non un chip colorato: un chip nuovo vorrebbe
 * una famiglia di colori che nessuno ha deciso, e `LEAD_STATUSES` porta già le chiavi i18n.
 *
 * Nessun cestino: come per il preventivo, un contatto non "esce" dalla board cancellandosi — cambia
 * colonna, o esce dalla pipeline. `draggable`: KbCard ferma il pointer sul titolo, così cliccarlo
 * apre il dettaglio invece di avviare il trascinamento.
 */

import { useTranslation } from 'react-i18next';
import { KbCard } from '../../components/KbCard';
import { PersonAvatar } from '../../components/PersonAvatar';
import type { Person } from './personMappers';
import { LEAD_STATUSES } from './personTypes';

export function PersonKanbanCard({ person }: { person: Person }) {
  const { t } = useTranslation();
  const { personId, firstName, lastName, location, leadStatus, emailAddresses, phones } = person;

  // Il recapito "principale" è quello marcato primary; senza marcatura vale il primo, che è
  // comunque meglio di una card muta. Il `?.` regge le risposte senza collezioni.
  const email = emailAddresses?.find((e) => e.primary)?.address ?? emailAddresses?.[0]?.address;
  const phone = phones?.find((p) => p.primary)?.number ?? phones?.[0]?.number;

  const leadLabel = LEAD_STATUSES.find((s) => s.value === leadStatus)?.label;

  return (
    <KbCard
      lead={<PersonAvatar firstName={firstName} lastName={lastName} />}
      title={`${firstName} ${lastName}`.trim() || `#${personId}`}
      titleTo={`/contacts/${personId}`}
      draggable
      meta={
        <>
          {location ? <span className="kb-card__tag">{location}</span> : <span />}
          {leadLabel && <span className="kb-card__tag">{t(leadLabel)}</span>}
        </>
      }
      foot={
        email || phone ? (
          <>
            <span className="kb-card__contact">{email ?? ''}</span>
            {phone && <span className="kb-card__contact">{phone}</span>}
          </>
        ) : undefined
      }
    />
  );
}
